import { zodResolver } from '@hookform/resolvers/zod';
import { CUSTOMER_LEVEL, ValidationErrorResponse } from '@wyz/types';
import { UseFormGetFieldState } from 'react-hook-form';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { CommonErrorCodesEnumType } from '~/common/types';
import { BrandTemplateFormBase } from '~/connect/components';
import { RedistributionBrandTemplateHelpers } from '~/connect/helpers';
import { redistributionBrandTemplateFormSchema } from '~/connect/schemas';
import {
  ICriteria,
  IRedistributionBrandTemplateForm,
  IRedistributionBrandTemplatePayload,
} from '~/connect/types';

const DEFAULT_VALUES: IRedistributionBrandTemplateForm = {
  default: false,
  name: '',
  criteria: [],
  brands: [],
  customerScope: [],
};

type Props =
  | {
      defaultValues?: Partial<IRedistributionBrandTemplateForm>;
      mode?: 'CREATE' | 'UPDATE';
      isLoading: boolean;
      onSubmit: (
        data: IRedistributionBrandTemplatePayload,
        formState: {
          criteria: ReturnType<
            UseFormGetFieldState<IRedistributionBrandTemplatePayload>
          >;
        },
      ) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: Partial<IRedistributionBrandTemplateForm>;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
    };

export function RedistributionBrandTemplateForm(props: Props) {
  const { mode = 'CREATE', defaultValues = DEFAULT_VALUES } = props;

  return (
    <BrandTemplateFormBase<IRedistributionBrandTemplateForm>
      config={{
        variant: 'SCOPED',
        featureContext: FEATURE_CONTEXT.REDISTRIBUTION_BRAND_TEMPLATE,
        cancelTo: '..',
        customerScope: {
          filterOption: (level) => level === CUSTOMER_LEVEL.N1,
        },
      }}
      resolver={zodResolver(redistributionBrandTemplateFormSchema)}
      defaultValues={defaultValues}
      mode={mode}
      submission={
        props.mode === 'READ_ONLY'
          ? undefined
          : {
              isLoading: props.isLoading,
              errors: props.errors,
              onSubmit: (
                formData: IRedistributionBrandTemplateForm,
                criteria: ICriteria[],
                formState,
              ) =>
                props.onSubmit?.(
                  RedistributionBrandTemplateHelpers.fromFormToPayload(
                    formData,
                    criteria,
                  ),
                  formState,
                ),
            }
      }
    />
  );
}
